import { getDeviceId } from '../getDeviceId';
import { hashPii } from '../hashPii/hashPii';
import { getAnalytics } from '../track';

type IdentifyUserProfile = {
	email: string;
	firstName?: string;
	lastName?: string;
};

/**
 * Identifies a user with OpenPanel while preserving their pre-identify device
 * history (see HB-954).
 *
 * No personal value leaves the browser in plain text (HB-1296): the email and
 * names are hashed with `hashPii` first, and the hashed email doubles as the
 * `profileId` — a hash is stable, so the same person still resolves to one
 * profile across devices. If the email cannot be hashed the user is not
 * identified at all.
 *
 * When a `device_id` cookie is present it:
 *  1. stamps `deviceId` onto every subsequent event via `setGlobalProperties`,
 *     giving the pipeline a stable key across anonymous + identified events, and
 *  2. `alias`es the device to the hashed email BEFORE `identify` flips the
 *     in-memory `profileId` over to it, so OpenPanel merges the anonymous device
 *     stream into the identified profile instead of splitting it in two.
 *
 * With no `device_id` cookie (flag off / cookie not yet set) it falls back to a
 * plain `identify`, matching the previous behaviour exactly.
 */
export const identifyUser = async ({
	email,
	firstName,
	lastName
}: IdentifyUserProfile): Promise<void> => {
	const op = getAnalytics();
	if (!op) return;

	const [hashedEmail, hashedFirstName, hashedLastName] = await Promise.all([
		hashPii(email),
		hashPii(firstName),
		hashPii(lastName)
	]);

	if (!hashedEmail) {
		return;
	}

	const deviceId = getDeviceId();

	if (deviceId) {
		op.setGlobalProperties({ deviceId });
		op.alias({ profileId: hashedEmail, alias: deviceId });
	}

	op.identify({
		profileId: hashedEmail,
		email: hashedEmail,
		...(hashedFirstName !== undefined ? { firstName: hashedFirstName } : {}),
		...(hashedLastName !== undefined ? { lastName: hashedLastName } : {})
	});
};
